// Pure rules for the Student Check-In wizard: the shape of the participant
// filter we send to the API, the per-step "you cannot proceed yet" messages and
// the email-template dropdown options. Kept out of the components so the rules
// can be unit-tested without rendering the wizard.

import {DAYS_PER_CYCLE} from './checkInFrequency'

export const CHECK_IN_STEP = {
  frequency: 0,
  date: 1,
  survey: 2,
  invites: 3,
  participants: 4,
}

export const defaultParticipantFilter = () => ({
  all: false,
  allStaff: false,
  allSchools: true,
  allStudent: true,
  schoolId: [],
  filterValues: [],
})

const asArray = value => {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined) return []
  return [value]
}

const firstDefined = (...values) => values.find(v => v !== undefined)

// The stored JSON is deserialised straight into the API's ParticipationFilter,
// which only understands the keys below. Earlier versions of the wizard wrote
// `allSchool` / `schoolIds` / `allStudents`, so saved check-ins still carry
// them and have to be adapted when one is opened for editing.
export const toCanonicalParticipantFilter = raw => {
  const filter = raw ?? {}
  return {
    all: filter.all === true,
    allStaff: filter.allStaff === true,
    allSchools: firstDefined(filter.allSchools, filter.allSchool) === true,
    allStudent: firstDefined(filter.allStudent, filter.allStudents) !== false,
    schoolId: asArray(firstDefined(filter.schoolId, filter.schoolIds)),
    filterValues: asArray(filter.filterValues),
  }
}

export const hasSelectedSchools = participantFilter =>
  toCanonicalParticipantFilter(participantFilter).schoolId.length > 0

// A single-school customer has exactly one school and no choice to make; a MAT
// counts every school it can reach while "All Schools" is on, and keeps the
// user's own selection (narrowed to what they may see) while it is off.
export const withSchoolScope = (
  participantFilter,
  {isMat, schoolId, accessibleSchoolIds} = {}
) => {
  const filter = toCanonicalParticipantFilter(participantFilter)

  if (!isMat) {
    return {
      ...filter,
      allSchools: true,
      schoolId: schoolId === null || schoolId === undefined ? [] : [schoolId],
    }
  }

  const accessible = asArray(accessibleSchoolIds)
  if (filter.allSchools) return {...filter, schoolId: [...accessible]}
  if (accessible.length === 0) return filter
  return {...filter, schoolId: filter.schoolId.filter(id => accessible.includes(id))}
}

// Returns the message explaining why the wizard cannot leave this step, or null
// when the step is satisfied.
export const stepBlockingMessage = (
  step,
  {frequency, surveyIds, participantFilter} = {}
) => {
  if (step === CHECK_IN_STEP.frequency && !(Number(frequency) > 0)) {
    return 'Please select frequency to proceed'
  }

  if (step === CHECK_IN_STEP.survey && asArray(surveyIds).length === 0) {
    return 'Please select at least one survey to proceed'
  }

  if (step === CHECK_IN_STEP.participants) {
    const filter = toCanonicalParticipantFilter(participantFilter)
    if (!filter.allSchools && !hasSelectedSchools(filter)) {
      return 'Please select at least one school to proceed'
    }
  }

  return null
}

// Pupils with no email address on record cannot be invited, and the school can
// only fix that before publishing. No readiness answer means no claim either way.
export const missingEmailWarning = inviteReadiness => {
  const withoutEmail = inviteReadiness?.withoutEmail ?? 0
  if (withoutEmail <= 0) return ''

  return `${withoutEmail} of ${inviteReadiness.pupils} pupils have no email address and will not receive the invitation.`
}

// The wizard's own default template is always offered first and is the object
// the Select compares against, so it must be spread into the list rather than
// wrapped, and must not appear twice when the API also returns it.
export const buildTemplateOptions = (defaultTemplate, fetched) => {
  const templates = asArray(fetched).filter(Boolean)
  if (!defaultTemplate) return templates

  const isDefault = template =>
    template.id !== undefined &&
    defaultTemplate.id !== undefined &&
    template.id === defaultTemplate.id

  return [defaultTemplate, ...templates.filter(t => !isDefault(t))]
}

const POOL_QUESTIONS_PER_INSTANCE = 5

// How many check-in surveys the chosen templates deal into: five pool
// questions per survey, the remainder in the last one (the same greedy split
// Custom-Api runs when it publishes).
export const projectedInstanceCount = surveys =>
  Math.ceil(
    (surveys ?? []).reduce(
      (total, survey) => total + (survey?.surveyQuestions?.length ?? 0),
      0
    ) / POOL_QUESTIONS_PER_INSTANCE
  )

// What the setup will look like once it runs: the day it starts, how many
// surveys one cycle takes and the day the first cycle is expected to end. The
// end is the eve of the launch that would follow the last survey; holidays
// and pauses can push it later, so it is only ever "expected". Null when the
// wizard does not yet know enough to say.
export const projectedCycle = ({startDate, frequency, surveys}) => {
  const instanceCount = projectedInstanceCount(surveys)
  const daysPerCycle = DAYS_PER_CYCLE[Number(frequency)]
  const start = startDate ? new Date(startDate) : null
  if (!instanceCount || !daysPerCycle || !start || Number.isNaN(start.getTime())) {
    return null
  }
  const end = new Date(start)
  end.setDate(end.getDate() + instanceCount * daysPerCycle - 1)
  return {startDate: start, instanceCount, endDate: end}
}
